import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { cloneDeep, isEmpty } from 'lodash';
import expandIcon from '../../../../assets/icons/expand.svg';
import collapseIcon from '../../../../assets/icons/collapse.svg';

function GradingDetail({
  isPageDisable,
  specTemplateData,
  specTempGradingLib,
  getSelectValue,
  handleSelect,
  t,
  catBasedOnPTData,
  setState,
  calculateMeasurement,
}) {
  const [isSizeRangeExpanded, setIsSizeRangeExpanded] = useState(true);
  const [localSizeRanges, setLocalSizeRanges] = useState([]);
  const [recalculateTrigger, setRecalculateTrigger] = useState(0);
  const lastProcessedTriggerRef = useRef(0);
  const calculateMeasurementRef = useRef(calculateMeasurement);
  const specTemplateDataRef = useRef(specTemplateData);

  useEffect(() => {
    calculateMeasurementRef.current = calculateMeasurement;
    specTemplateDataRef.current = specTemplateData;
  }, [calculateMeasurement, specTemplateData]);

  useEffect(() => {
    if (specTemplateData?.sizeRanges) {
      if (Array.isArray(specTemplateData.sizeRanges)) {
        setLocalSizeRanges(cloneDeep(specTemplateData.sizeRanges));
      } else if (specTemplateData.sizeRanges) {
        const parsedSizeRanges = JSON.parse(specTemplateData.sizeRanges);
        if (Array.isArray(parsedSizeRanges)) {
          setLocalSizeRanges(cloneDeep(parsedSizeRanges));
        }
      }
    }
  }, [specTemplateData?.sizeRanges]);

  useEffect(() => {
    if (recalculateTrigger > 0 && recalculateTrigger !== lastProcessedTriggerRef.current && calculateMeasurementRef.current) {
      lastProcessedTriggerRef.current = recalculateTrigger;
      const pomLibrariesLength = specTemplateDataRef.current?.pomLibraries?.length || 0;

      if (pomLibrariesLength > 0) {
        setTimeout(() => {
          const calculationPromises = [];
          for (let i = 0; i < pomLibrariesLength; i += 1) {
            calculationPromises.push(calculateMeasurementRef.current(i));
          }
          Promise.all(calculationPromises).catch((error) => {
            console.error('Error calculating measurements:', error);
          });
        }, 100);
      }
    }
  }, [recalculateTrigger]);

  const swapSampleSizeValues = (pomItem, currentSampleSizeName, newSampleSizeName) => {
    if (!pomItem?.gradings || !Array.isArray(pomItem.gradings)) {
      return pomItem;
    }

    const clonedItem = cloneDeep(pomItem);
    const currentSampleGrading = clonedItem.gradings.find(
      (grad) => grad.sizeName === currentSampleSizeName && (grad.isZero === true || grad.isSampleSize === true),
    );
    const newSampleGrading = clonedItem.gradings.find((grad) => grad.sizeName === newSampleSizeName);

    if (currentSampleGrading && newSampleGrading) {
      const currentGradingValue = currentSampleGrading.grading;
      const newGradingValue = newSampleGrading.grading;

      currentSampleGrading.grading = newGradingValue;
      currentSampleGrading.isZero = false;
      currentSampleGrading.isSampleSize = false;

      newSampleGrading.grading = currentGradingValue;
      newSampleGrading.isZero = true;
      newSampleGrading.isSampleSize = true;
    } else if (newSampleGrading) {
      const currentGradingValue = currentSampleGrading?.grading || '0';
      if (currentSampleGrading) {
        currentSampleGrading.isZero = false;
        currentSampleGrading.isSampleSize = false;
      }
      newSampleGrading.grading = currentGradingValue;
      newSampleGrading.isZero = true;
      newSampleGrading.isSampleSize = true;
    }

    return clonedItem;
  };

  const handleSampleSizeChange = (index) => {
    if (isPageDisable) {
      return;
    }

    const newSampleSize = localSizeRanges[index];
    const currentSampleSize = localSizeRanges.find((item) => item.isSampleSize);
    const currentSampleSizeName = currentSampleSize?.name;
    const newSampleSizeName = newSampleSize?.name;

    if (!newSampleSizeName || currentSampleSizeName === newSampleSizeName) {
      return;
    }

    let updatedPomLibraries = specTemplateData?.pomLibraries || [];

    if (!isEmpty(specTemplateData?.pomLibraries) && currentSampleSizeName) {
      updatedPomLibraries = specTemplateData.pomLibraries.map((pomItem) => swapSampleSizeValues(pomItem, currentSampleSizeName, newSampleSizeName));
    }

    const updatedSizeRanges = localSizeRanges.map((item, i) => ({
      ...item,
      isSampleSize: i === index,
      isZero: i === index,
    }));

    if (specTempGradingLib) {
      specTempGradingLib.sizeRanges = updatedSizeRanges;
      specTempGradingLib.sampleSize = updatedSizeRanges.find((item) => item.isSampleSize)?.name || null;
    }

    setState((prevState) => ({
      ...prevState,
      specTemplateData: {
        ...prevState.specTemplateData,
        sizeRanges: updatedSizeRanges,
        pomLibraries: updatedPomLibraries,
      },
    }));
    setLocalSizeRanges(updatedSizeRanges);

    if (calculateMeasurement && updatedPomLibraries.length > 0) {
      setRecalculateTrigger((prev) => prev + 1);
    }
  };

  const sampleSizeName = localSizeRanges?.find((item) => item.isSampleSize)?.name || null;

  return (
    <>
      <Col md={6}>
        <div className="common-input-control">
          <p className="field-name">{t('SPEC_TEMP_LIBRARY.GRADING_DESC')}</p>
          <input type="text" name="specDescription" disabled value={specTempGradingLib?.description || ''} className="form-control" />
        </div>
      </Col>
      <Col md={3}>
        <div className="common-react-select">
          <p className="field-name">{t('SPEC_TEMP_LIBRARY.BRAND')}</p>
          <Select
            options={specTempGradingLib?.brand || []}
            value={getSelectValue(specTempGradingLib?.brand, specTemplateData?.brandId) || null}
            onChange={(e) => handleSelect(e, 'brandId')}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option._id}
            className="common-select"
            classNamePrefix="common-select"
            placeholder="Select Brand"
            isDisabled={isPageDisable}
            isClearable
          />
        </div>
      </Col>
      <Col md={3}>
        <div className="common-react-select">
          <p className="field-name">{t('COLOUR_LIBRARYS.COLOUR_LIBRARY_GROUP.ADD_EDIT_COLOUR_GROUP.SUBBRAND')}</p>
          <Select
            id="subBrand"
            options={specTempGradingLib?.subBrand?.filter((sb) => sb?.isActive) || []}
            value={getSelectValue(specTempGradingLib?.subBrand, specTemplateData?.subBrandId) || null}
            onChange={(e) => handleSelect(e, 'subBrandId')}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option._id}
            className="common-select"
            classNamePrefix="common-select"
            placeholder={t('COLOUR_LIBRARYS.COLOUR_LIBRARY_GROUP.ADD_EDIT_COLOUR_GROUP.SELECT_SUBBRAND')}
            isDisabled={isPageDisable}
            isClearable
          />
        </div>
      </Col>
      {catBasedOnPTData && (
        <Col md={3}>
          <div className="common-input-control">
            <p className="field-name">{t('SPEC_TEMP_LIBRARY.PRODUCT_TYPE')}</p>
            <input
              type="text"
              name="productType"
              disabled
              value={specTempGradingLib?.productTypeId?.map((pt) => pt.name).join(', ') || ''}
              className="form-control"
            />
          </div>
        </Col>
      )}
      <Col md={3}>
        <div className="common-input-control">
          <p className="field-name">{t('SPEC_TEMP_LIBRARY.CATEGORY1')}</p>
          <input type="text" name="catOne" disabled value={specTempGradingLib?.catOne?.map((cat) => cat.name).join(', ') || ''} className="form-control" />
        </div>
      </Col>
      <Col md={3}>
        <div className="common-input-control">
          <p className="field-name">{t('SPEC_TEMP_LIBRARY.CATEGORY2')}</p>
          <input type="text" name="catTwo" disabled value={specTempGradingLib?.catTwo?.map((cat) => cat.name).join(', ') || ''} className="form-control" />
        </div>
      </Col>
      <Col md={6}>
        <Row>
          <Col md={4}>
            <div className="common-react-select">
              <p className="field-name">{t('SPEC_TEMP_LIBRARY.CATEGORY3')}</p>
              <Select
                options={specTempGradingLib?.catThree || []}
                value={getSelectValue(specTempGradingLib?.catThree, specTemplateData?.catThreeId) || null}
                onChange={(e) => handleSelect(e, 'catThreeId')}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                className="common-select"
                classNamePrefix="common-select"
                placeholder={t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER')}
                isDisabled={isPageDisable}
                isClearable
              />
            </div>
          </Col>
          <Col md={8}>
            {localSizeRanges && Array.isArray(localSizeRanges) && localSizeRanges.length > 0 && (
              <div className="size-grading mt-2">
                {isSizeRangeExpanded ? (
                  <>
                    <span className="f-16 text-bold mr-1">{t('POMS.GRADING_GROUP.SIZE_RANGE_GRADING.SIZES') || t('COMMON_LABEL.SIZE') || 'Sizes'}</span>
                    <button
                      type="button"
                      className="btn p-0 mr-16"
                      onClick={() => setIsSizeRangeExpanded(false)}
                      disabled={isPageDisable}
                      aria-label={t('POMS.GRADING_GROUP.SIZE_RANGE_GRADING.COLLAPSE') || 'Collapse'}
                    >
                      <img src={collapseIcon} alt="collapse" />
                    </button>
                    <div className="size-input-container d-inline-flex">
                      {localSizeRanges.map((item, index) => {
                        const sizeName = item?.name || '';
                        const isSampleSize = item?.isSampleSize || false;
                        const uniqueKey = `sizeRange_${sizeName}_${index}`;
                        return (
                          <div key={uniqueKey} className="d-flex align-items-center position-relative mr-8">
                            <p className="custom-radio style-green sm mb-0 mr-8">
                              <input
                                id={uniqueKey}
                                type="radio"
                                name="SampleSize"
                                className="ml-8 sample-size-radio"
                                checked={isSampleSize}
                                value={isSampleSize}
                                onChange={() => handleSampleSizeChange(index)}
                                disabled={isPageDisable}
                                aria-label={sizeName}
                              />
                              <label htmlFor={uniqueKey} />
                            </p>
                            <input
                              type="text"
                              value={sizeName || ''}
                              className={`form-control size-input ${isSampleSize ? 'sample-size-input' : ''}`}
                              disabled
                              placeholder={t('POMS.GRADING_GROUP.SIZE_RANGE_GRADING.SIZE_RANGE')}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="d-flex align-items-center">
                    <span className="f-16 text-bold mr-1">{t('POMS.GRADING_GROUP.SIZE_RANGE_GRADING.SIZES') || t('COMMON_LABEL.SIZE') || 'Sizes'}</span>
                    <button
                      type="button"
                      className="btn p-0 mr-16"
                      onClick={() => setIsSizeRangeExpanded(true)}
                      disabled={isPageDisable}
                      aria-label={t('POMS.GRADING_GROUP.SIZE_RANGE_GRADING.EXPAND') || 'Expand'}
                    >
                      <img src={expandIcon} alt="expand" />
                    </button>
                    {sampleSizeName && <span className="sample-size-span">{sampleSizeName}</span>}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Col>
    </>
  );
}

export default GradingDetail;
